import React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { List, useDataGrid } from "@refinedev/mui";

// One row of GET /api/v2/admin/characters — a character from the creator's
// library, which belongs to no story. The endpoint deliberately does NOT send
// the sheet's prose (personality, backstory, the runtime system prompt): this
// panel is a register of who exists, not a place to read a creator's writing in
// bulk. `hasSystemPrompt` carries the one bit of that worth seeing.
type CharacterRecord = {
  id: number;
  name: string;
  role: string;
  creatorId: number;
  creatorName: string | null;
  creatorEmail: string | null;
  avatarUrl: string | null;
  visualStyle: string | null;
  hasSystemPrompt: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

export const CharacterList = () => {
  // Server-ordered: newest first. Columns are not sortable — the shared data
  // provider sends page/limit only, so client-side sorting would reorder just
  // the current page.
  const { dataGridProps } = useDataGrid({});

  const columns = React.useMemo<GridColDef<CharacterRecord>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        minWidth: 80,
        maxWidth: 100,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Typography variant="body2">
            <code>{row.id}</code>
          </Typography>
        ),
      },
      {
        field: "avatarUrl",
        headerName: "Image",
        minWidth: 80,
        sortable: false,
        display: "flex",
        // Null until a portrait renders — a character is written first and
        // drawn later, so an empty slot here is a normal state, not a fault.
        renderCell: ({ row }) =>
          row.avatarUrl ? (
            <Link href={row.avatarUrl} target="_blank" rel="noreferrer">
              <Avatar
                variant="rounded"
                src={row.avatarUrl}
                alt={row.name}
                // Top-aligned for the same reason the creator's own grid is: a
                // character render is a standing figure, so a centred crop of a
                // 48px box is chest and nothing else.
                imgProps={{ style: { objectPosition: "top" } }}
                sx={{ width: 48, height: 48 }}
              />
            </Link>
          ) : (
            <span style={{ opacity: 0.6 }}>-</span>
          ),
      },
      {
        field: "name",
        headerName: "Character",
        flex: 1.4,
        minWidth: 200,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Tooltip title={row.name}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={row.deletedAt ? { textDecoration: "line-through", opacity: 0.6 } : undefined}
                >
                  {row.name}
                </Typography>
              </Tooltip>
              {row.deletedAt ? (
                <Tooltip title={`Deleted ${formatDateTime(row.deletedAt)}`}>
                  <Chip size="small" label="deleted" variant="outlined" />
                </Tooltip>
              ) : null}
            </Stack>
            {row.role ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.role}
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      {
        field: "creatorId",
        headerName: "Creator",
        flex: 1,
        minWidth: 190,
        sortable: false,
        display: "flex",
        // The id is the fact and the name is the convenience, so the id is
        // always shown — the join is LEFT and the name can be missing.
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {row.creatorName ?? <span style={{ opacity: 0.6 }}>(unknown)</span>}
            </Typography>
            <Tooltip title={row.creatorEmail ?? ""}>
              <Typography variant="caption" color="text.secondary" noWrap>
                <code>{row.creatorId}</code>
                {row.creatorEmail ? ` · ${row.creatorEmail}` : ""}
              </Typography>
            </Tooltip>
          </Stack>
        ),
      },
      {
        field: "visualStyle",
        headerName: "Style",
        minWidth: 150,
        sortable: false,
        display: "flex",
        // What a portrait would be drawn in. Absent until the creator picks one,
        // which is also why the character has no image yet.
        renderCell: ({ row }) =>
          row.visualStyle ? (
            <Chip size="small" label={row.visualStyle} variant="outlined" />
          ) : (
            <span style={{ opacity: 0.6 }}>-</span>
          ),
      },
      {
        field: "hasSystemPrompt",
        headerName: "Refined",
        minWidth: 110,
        sortable: false,
        display: "flex",
        // Whether Foundry has minted this character's runtime system prompt.
        renderCell: ({ row }) =>
          row.hasSystemPrompt ? (
            <Chip size="small" label="yes" color="success" variant="outlined" />
          ) : (
            <span style={{ opacity: 0.6 }}>-</span>
          ),
      },
      {
        field: "createdAt",
        headerName: "Created",
        minWidth: 180,
        sortable: false,
        display: "flex",
        valueFormatter: (value) =>
          formatDateTime(value as string | null | undefined),
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        minWidth: 180,
        sortable: false,
        display: "flex",
        // Differs from Created on any character that has been edited since —
        // and every field on the sheet saves itself, so it moves often.
        valueFormatter: (value) =>
          formatDateTime(value as string | null | undefined),
      },
    ],
    []
  );

  return (
    <List canCreate={false}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Characters from every creator's library — the ones that belong to no
          story, newest first. Read-only: these are a creator's own working
          material, not published content.
        </Typography>
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        disableColumnFilter
        rowHeight={68}
      />
    </List>
  );
};
